/**
 * 가격 정책 정본 — 환율·마진율·반올림 단위·번들링 기준을 **여기 하나로만 읽는다.**
 *
 * 정본 순서는 두 층이다.
 *   1) 상품에 저장된 `product.pricing.info` — 셀러가 이 상품에 대해 명시한 값
 *   2) 화면 기본세팅(설정 > 기본세팅) — 회사별 기본 정책. 1)에 없는 항목만 여기서 온다
 *
 * ⚠ 왜 층으로 쌓는가 (섞는다)
 *   `pricing.info` 에는 셀러가 바꾼 항목만 들어 있을 수 있다(rocket.sourcing.setPrice 는 보낸 항목만 덮는다).
 *   예전처럼 "info 가 있으면 info 만" 으로 고르면, 환율만 바꿔 보낸 순간 마진율이 회사 정책이 아니라
 *   코드 기본값 40% 로 떨어진다. 그래서 화면 기본세팅을 **바닥에 깔고** info 를 위에 얹는다.
 *   info 가 완전한 상품에서는 결과가 예전과 똑같다(얹히는 값이 전부라 바닥이 보이지 않는다).
 *
 * ⚠ 왜 이 파일이 생겼는가 (2026-09-19 실측)
 *   가격 작업(pricingService)은 화면 기본세팅만 읽었고, rocket.sourcing.setPrice 는 `pricing.info` 에만 썼다.
 *   그래서 환율 210 을 넣어 매입 4,200 을 받아놓고 작업개시를 돌리면 매입이 7,000(=350) 으로
 *   조용히 되돌아갔다. 경고도 없었다 — 성공처럼 보이는 실패다.
 *   ⇒ 정책을 읽는 곳은 전부 이 함수를 통한다. 화면 기본세팅을 직접 읽지 말 것.
 *
 * msrpMultiplier 는 일부러 상품 설정을 보지 않는다. 화면(PricingEdit.calculateMsrpValue)이
 * 기본세팅에서만 읽고 있어서, 여기서 상품값을 우선하면 화면과 계산이 갈린다.
 */

// 아무 설정도 없을 때의 마지막 안전망. 화면 기본세팅이 비어 있는 계정에서만 보인다.
const FALLBACK = {
  exchangeRate: 350,
  marginRate: 40,
  coupangMarginRate: 40,
  roundUnit: 100,
  enableMinSupplyMargin: false,
  minSupplyMarginAmount: 3000,
  enableBundling: false,
  bundlingType: 'marginAmount',
  minMarginAmount: 3000,
  minCoupangMarginAmount: 3000,
  msrpMultiplier: 1.3
};

// 쿠패스 통합통관 환율 모드 (기본세팅 useIntegratedRate) — 소싱 관문(CLI margin.js ROCKET_GATE)과 같은 공식.
//   매입가 = (CNY × N × 통합통관환율 + 포장검수 200 + 바코드 100) × 1.1(관부가세)
// 환율은 서버(ExchangeRates {type:'INTEGRATED'}, /api/getExchangeRate)에서 받아 setIntegratedRate 로 넣는다.
// 기본세팅에 저장하지 않는다 — 저장하면 서버 환율이 바뀌어도 옛 값으로 계산된다.
const INTEGRATED_COST = { packing: 200, barcode: 100, vat: 1.1 };
let integratedRate = null;

// 소싱 목표 판매가 상품의 공급마진 기준(판 #389) — 소싱 관문(서버 tool-exec/margin-calc.js normalizeGate·supplyMarginVerdict)과 같은 규칙.
// 기본 = 공급마진율 25% 이상 · 고객 설정(Settings.rocketMarginGate = store.state.settings[0].rocketMarginGate)이 있으면 그것.
// 서버와 갈라지면 tool-exec/__tests__/margin-calc.js «화면 = 서버» 대조가 깨진다.
const MARGIN_GATE_MODES = ['rate', 'amount', 'both', 'either'];
const DEFAULT_MARGIN_GATE = Object.freeze({ mode: 'rate', minRate: 25, minAmount: null, source: 'default' });

export default {
  FALLBACK,
  INTEGRATED_COST,

  /** 서버에서 받은 통합통관환율을 넣는다. 못 받았으면 null. */
  setIntegratedRate(rate) {
    integratedRate = Number(rate) > 0 ? Number(rate) : null;
  },

  getIntegratedRate() {
    return integratedRate;
  },

  /**
   * 매입가(원). cnyTotal = 개당 CNY × 묶음 수량.
   * 통합통관 모드면 소싱과 같은 공식, 아니면 CNY × 적용환율.
   * 통합통관 모드인데 환율을 아직 못 받았으면 적용환율로 계산하고 콘솔에 남긴다.
   */
  purchaseCost(cnyTotal, policy) {
    const cny = Number(cnyTotal) || 0;
    if (policy && policy.useIntegratedRate) {
      if (policy.integratedRate > 0) {
        return Math.round((cny * policy.integratedRate + INTEGRATED_COST.packing + INTEGRATED_COST.barcode) * INTEGRATED_COST.vat);
      }
      console.error('[pricingPolicy] 통합통관 환율 모드인데 환율을 받지 못해 적용환율로 계산합니다.');
    }
    return cny * policy.exchangeRate;
  },

  /**
   * 상품 하나에 적용할 가격 정책을 돌려준다.
   *
   * @param {Object|null} product - 상품 객체. null 이면 화면 기본세팅만 쓴다(상품 생성 직전 등)
   * @param {Object|null} screenSettings - 화면 기본세팅 (store.state.settings[0].defaultSettings 또는 AIRocketReg.defaultSettings)
   * @returns {Object} 위 FALLBACK 과 같은 키를 모두 채운 정책 객체
   */
  resolve(product, screenSettings) {
    const screen = screenSettings || {};
    const saved = (product && product.pricing && product.pricing.info) ? product.pricing.info : {};

    // 화면 기본세팅을 바닥에 깔고 상품 설정을 얹는다. undefined 는 얹히지 않는다.
    const base = { ...screen };
    for (const key of Object.keys(saved)) {
      if (saved[key] !== undefined && saved[key] !== null) base[key] = saved[key];
    }

    // `||` 로 기본값을 채우는 건 기존 코드(bundleOptionService.resolveBundleSettings,
    // PricingEdit.loadDefaultSettings, pricingService.getDefaultPricingInfo)와 같은 규칙이다.
    // 단, 마진율·쿠팡마진율·최소공급마진액은 0 이 뜻 있는 값이다. `||` 로 채우면 rocket.sourcing.setPrice 로
    // 0 을 넣어도 다음 가격 작업에서 FALLBACK(40% 등)으로 조용히 되돌아간다 — 비어 있을 때만 채운다.
    // (환율·반올림 단위는 0 이면 계산이 깨지므로 그대로 `||`)
    const orFallback = (value, fallback) => (value === undefined || value === null || value === '' ? fallback : value);
    return {
      exchangeRate: base.exchangeRate || FALLBACK.exchangeRate,
      marginRate: orFallback(base.marginRate, FALLBACK.marginRate),
      coupangMarginRate: orFallback(base.coupangMarginRate, FALLBACK.coupangMarginRate),
      roundUnit: base.roundUnit || FALLBACK.roundUnit,
      enableMinSupplyMargin: base.enableMinSupplyMargin || false,
      minSupplyMarginAmount: orFallback(base.minSupplyMarginAmount, FALLBACK.minSupplyMarginAmount),
      enableBundling: base.enableBundling || false,
      bundlingType: base.bundlingType || FALLBACK.bundlingType,
      minMarginAmount: base.minMarginAmount || FALLBACK.minMarginAmount,
      minCoupangMarginAmount: base.minCoupangMarginAmount || FALLBACK.minCoupangMarginAmount,
      // 상품 설정을 보지 않는다 (위 주석 참고)
      msrpMultiplier: screen.msrpMultiplier || FALLBACK.msrpMultiplier,
      // 통합통관 모드는 회사 기본세팅 스위치 하나로 정한다 (상품별로 갈리면 같은 화면 숫자가 섞인다)
      useIntegratedRate: !!screen.useIntegratedRate,
      integratedRate
    };
  },

  /**
   * 소싱에서 정한 목표 판매가(product.sourcingSellingPrice)로 옵션 가격을 만든다.
   *   판매가 = 목표 판매가 그대로 (묶음 N 은 이미 반영된 값)
   *   공급가 = 소싱에서 실어 온 공급가(sourcingSupplyPrice)가 있으면 **그 값 그대로** (재계산·반올림 없음)
   *           없으면 판매가 × (1 − 쿠팡마진율), roundUnit 으로 반올림 (하위호환)
   * 원칙: 판매가·공급가·매입원가는 소싱(CLI rocket.sourcing.calculateMargin)에서 확정하고 등록은 집행만 한다.
   * 그래서 마진 기준 검사도 소싱과 같은 기준(고객 설정 · 없으면 25%)으로 한다 — 소싱에서 통과한 상품이 여기서 떨어지지 않는다.
   * 마진율·최소공급마진 보정은 쓰지 않는다. 매입가는 마진 확인용이다.
   *
   * 공급마진(공급가 − 매입가)이 마진 기준(marginGate · 없으면 판매가의 25%)에 못 미치면 가격을 만들지 않고 실패한다 —
   * 소싱 관문(CLI rocket.sourcing.calculateMargin)에서 탈락할 건이지 등록 대상이 아니라, 여기까지 왔으면 앞 단계의 버그다.
   * 매입가는 소싱에서 실어 온 매입원가(sourcingPurchaseCost)가 있으면 그것, 없으면 CNY × 환율 × N 을 부르는 쪽이 넘긴다.
   *
   * @param {number} sellingPrice - 목표 판매가(원)
   * @param {number} purchasePrice - 매입가(CNY × 환율 × 묶음 수량)
   * @param {Object} policy - resolve() 결과 (coupangMarginRate, roundUnit 을 쓴다)
   * @param {string} label - 에러 메시지에 붙일 옵션 표시
   * @param {number|null} fixedSupplyPrice - 소싱 공급가(sourcingSupplyPrice). 있으면 그대로 쓴다
   * @param {Object|null} marginGate - 고객 마진 기준(store.state.settings[0].rocketMarginGate). 없으면 기본 25%
   */
  /** 저장된 마진 기준 → 판정 기준 하나. 없으면 기본 25% · 꼴 틀림은 던진다(기본으로 안 돌림) */
  marginGate(raw) {
    if (raw === undefined || raw === null) return DEFAULT_MARGIN_GATE;
    const bad = (why) => {
      console.error('[pricingPolicy] 마진 기준 설정 꼴 틀림:', why, raw);
      throw new Error(`마진 기준 설정이 잘못됐습니다(${why}) — 설정 화면 «로켓 소싱 마진 기준»에서 다시 저장하세요. 판정하지 않았습니다.`);
    };
    if (typeof raw !== 'object' || Array.isArray(raw)) bad('꼴');
    if (!MARGIN_GATE_MODES.includes(raw.mode)) bad(`mode ${JSON.stringify(raw.mode)}`);
    const minRate = raw.mode === 'amount' ? null : Number(raw.minRate);
    if (minRate !== null && !(raw.minRate !== '' && raw.minRate !== null && Number.isFinite(minRate) && minRate >= 0 && minRate < 100)) bad(`minRate ${JSON.stringify(raw.minRate)}`);
    const minAmount = raw.mode === 'rate' ? null : Number(raw.minAmount);
    if (minAmount !== null && !(raw.minAmount !== '' && raw.minAmount !== null && Number.isInteger(minAmount) && minAmount >= 0)) bad(`minAmount ${JSON.stringify(raw.minAmount)}`);
    return { mode: raw.mode, minRate, minAmount, source: 'customer' };
  },

  /** 기준 글 — 서버 gateText 와 같은 말 */
  marginGateText(g) {
    const r = `공급마진율 ${g.minRate}% 이상`;
    const a = `공급마진 ${Number(g.minAmount).toLocaleString('ko-KR')}원 이상`;
    return { rate: r, amount: a, both: `${r} 그리고 ${a}`, either: `${r} 또는 ${a}` }[g.mode];
  },

  /** 공급마진 통과 여부 — 서버 supplyMarginVerdict 의 pass 와 같은 식 */
  supplyMarginPasses(sellingPrice, supplyMargin, g) {
    const okRate = g.minRate !== null && supplyMargin >= sellingPrice * (g.minRate / 100);
    const okAmount = g.minAmount !== null && supplyMargin >= g.minAmount;
    return { rate: okRate, amount: okAmount, both: okRate && okAmount, either: okRate || okAmount }[g.mode];
  },

  fixedSellingPricing(sellingPrice, purchasePrice, policy, label = '', fixedSupplyPrice = null, marginGate = null) {
    const gate = this.marginGate(marginGate);
    let supplyPrice;
    if (Number(fixedSupplyPrice) > 0) {
      supplyPrice = Number(fixedSupplyPrice);
    } else {
      const coupangRate = Number(policy.coupangMarginRate) / 100;
      const roundUnit = Number(policy.roundUnit) || FALLBACK.roundUnit;
      supplyPrice = Math.round(sellingPrice * (1 - coupangRate) / roundUnit) * roundUnit;
    }
    if (!this.supplyMarginPasses(sellingPrice, supplyPrice - purchasePrice, gate)) {
      throw new Error(`${label ? label + ' ' : ''}공급마진이 마진 기준(${this.marginGateText(gate)})에 못 미쳐 가격을 쓰지 않았습니다: 목표 판매가 ${sellingPrice}원 → 공급가 ${supplyPrice}원 − 매입가 ${Math.round(purchasePrice)}원 = 공급마진 ${Math.round(supplyPrice - purchasePrice)}원. 소싱에서 탈락할 건입니다.`);
    }
    return {
      sellingPrice,
      supplyPrice,
      supplyMargin: supplyPrice - purchasePrice,
      sellingMargin: sellingPrice - supplyPrice
    };
  },

  /**
   * 소싱 목표가의 기준 옵션 = 개당 CNY 가 가장 싼 옵션. 소싱은 이 옵션으로 판매가·매입원가·공급가를 정해 온다.
   * @returns {number|null} 가장 싼 개당 CNY (값이 있는 옵션이 없으면 null)
   */
  referenceCny(options) {
    const prices = (options || []).map(o => Number(o && o.price)).filter(p => p > 0);
    return prices.length ? Math.min(...prices) : null;
  },

  /**
   * 소싱 매입원가(sourcingPurchaseCost)를 옵션의 묶음 수량 기준으로 바꾼다.
   * 소싱 매입원가는 **소싱 묶음 수량(sourcingBundleQuantity, 없으면 1) SKU 하나의 값**이다.
   * 그대로 쓰면 묶음을 1→2 로 바꿔도 매입가가 1개분에 머물러, 목표 판매가 고정 상품의
   * 역마진 검사를 그냥 통과한다(2026-09-28 실측). 개당 원가 × 묶음 수량으로 맞춘다.
   * SKU 단위 고정비까지 N배가 돼 실제보다 약간 높게 잡힌다 — 검사가 느슨해지는 쪽은 아니다.
   *
   * @param {number} purchaseCost - 소싱 매입원가(원). 없으면 null 을 돌려준다
   * @param {number} costBundleQuantity - 그 원가가 계산된 묶음 수량(소싱 묶음 수량, 없으면 1)
   * @param {number} bundleQuantity - 옵션의 묶음 수량
   */
  bundledPurchaseCost(purchaseCost, costBundleQuantity, bundleQuantity) {
    const cost = Number(purchaseCost);
    if (!(cost > 0)) return null;
    const base = Number(costBundleQuantity) >= 1 ? Number(costBundleQuantity) : 1;
    const n = Number(bundleQuantity) >= 1 ? Number(bundleQuantity) : 1;
    return n === base ? cost : Math.round(cost / base * n);
  },

  /**
   * 소싱 목표가를 옵션 하나에 맞춘다. 한 식으로 두 가지를 한다.
   *   1) 묶음 — 소싱 매입원가를 옵션 묶음 수량 기준으로 환산한다(bundledPurchaseCost, costBundleQuantity 기준)
   *   2) 옵션 — 기준 옵션(가장 싼 옵션)은 1)의 값 그대로. 더 비싼 옵션은 판매가를 개당 CNY 비율만큼
   *      올리고(roundUnit 올림), 매입원가에 두 옵션 매입가(purchaseCost, 같은 묶음 수량)의 차이만 더한다 —
   *      운임·검수 같은 고정분은 옵션이 비싸져도 같다. 공급가는 올린 판매가에서 다시 푼다(null).
   * refCny 가 없으면 2)는 건너뛰고 1)만 한다.
   */
  scaleFixedPricing({ sellingPrice, purchaseCost, supplyPrice }, optionCny, refCny, bundleQuantity, policy, costBundleQuantity = null) {
    const cny = Number(optionCny);
    const ref = Number(refCny);
    const n = Number(bundleQuantity) || 1;
    const baseCost = this.bundledPurchaseCost(purchaseCost, costBundleQuantity, n);
    if (!(cny > 0) || !(ref > 0) || cny <= ref) {
      return { sellingPrice, purchaseCost: baseCost !== null ? baseCost : purchaseCost, supplyPrice };
    }
    const roundUnit = Number(policy.roundUnit) || FALLBACK.roundUnit;
    return {
      sellingPrice: Math.ceil(Number(sellingPrice) * cny / ref / roundUnit) * roundUnit,
      purchaseCost: baseCost !== null
        ? baseCost + this.purchaseCost(cny * n, policy) - this.purchaseCost(ref * n, policy)
        : purchaseCost,
      supplyPrice: null
    };
  },

  /**
   * 정책이 상품에 저장된 것인지(= 셀러가 명시했는지) 알려준다.
   * 응답에 "이 설정이 다음 가격 작업에도 쓰인다" 를 정직하게 적기 위한 값이다.
   */
  hasProductPolicy(product) {
    return !!(product && product.pricing && product.pricing.info &&
      Object.keys(product.pricing.info).length > 0);
  }
};
